import { useState } from 'react';
import { Connection } from '../../services/connectionService';
import { harvestService } from '../../services/harvestService';
import { useConfirm } from '../../hooks/useConfirm';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { HarvestClientImportModal } from './HarvestClientImportModal';

interface HarvestConnectionProps {
  connection?: Connection;
  onUpdate: () => void;
}

export function HarvestConnection({ connection, onUpdate }: HarvestConnectionProps) {
  const [showImportModal, setShowImportModal] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();
  
  const handleConnect = async () => {
    try {
      await harvestService.startOAuthFlow();
    } catch (error) {
      console.error('Failed to start Harvest OAuth:', error);
      toast.error('Failed to connect to Harvest');
    }
  };
  
  const handleDisconnect = async () => {
    if (!connection) return;
    
    const confirmed = await confirm({
      title: 'Disconnect Harvest?',
      message: 'This will remove the connection and you will need to re-authenticate. Cached estimates will be retained.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    
    if (!confirmed) return;
    
    const toastId = toast.loading('Disconnecting...');
    
    try {
      await harvestService.deleteConnection(connection.id);
      toast.success('Harvest disconnected', { id: toastId });
      onUpdate();
    } catch (error) {
      console.error('Failed to disconnect:', error);
      toast.error('Failed to disconnect', { id: toastId });
    }
  };

  return (
    <>
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {/* Harvest Logo */}
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm0 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10-4.5 10-10 10z"/>
                  <circle cx="12" cy="8" r="2"/>
                  <path d="M12 11c-3 0-5 2-5 4v2h10v-2c0-2-2-4-5-4z"/>
                </svg>
              </div>
              
              {/* Connection Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Harvest</h3>
                <p className="text-sm text-gray-500">Import approved estimates to quickly generate SOWs</p>
              </div>
            </div>
            
            {/* Status Badge */}
            {connection?.status === 'active' && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                <CheckCircleIcon className="w-4 h-4" />
                Connected
              </div>
            )}
            {connection?.status === 'error' && (
              <div className="flex items-center gap-2 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm font-medium">
                <ExclamationTriangleIcon className="w-4 h-4" />
                Error
              </div>
            )}
          </div>
          
          {/* Connection Details */}
          {connection && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
              {connection.config?.company && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-medium">Account:</span>
                  <span className="text-gray-900">{connection.config.company}</span>
                </div>
              )}
              
              {connection.lastSyncAt && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Last synced:</span>
                  <span className="text-gray-900">
                    {new Date(connection.lastSyncAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Description when not connected */}
          {!connection && (
            <p className="text-gray-600 mb-4 text-sm">
              Connect to Harvest to import approved estimates and client data for faster contract creation.
            </p>
          )}
          
          {/* Actions */}
          <div className="flex gap-3">
            {!connection ? (
              <button
                onClick={handleConnect}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                Connect to Harvest
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowImportModal(true)}
                  disabled={connection.status !== 'active'}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserPlusIcon className="w-4 h-4 mr-2" />
                  Import Clients
                </button>
                
                <button
                  onClick={handleDisconnect}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Disconnect
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Help Section */}
        {!connection && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Setup Instructions:</h4>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Create a Harvest OAuth application in your Harvest account settings</li>
              <li>Add redirect URI: <code className="text-xs bg-gray-200 px-1 rounded">{window.location.origin}/settings/connections/harvest/callback</code></li>
              <li>Copy your Client ID and Client Secret to your environment variables</li>
              <li>Click "Connect to Harvest" above</li>
            </ol>
          </div>
        )}
      </div>
      
      <ConfirmDialog />
      
      {/* Client Import Modal */}
      <HarvestClientImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => {
          toast.success('Clients imported! Check the Clients page.');
          setShowImportModal(false);
        }}
      />
    </>
  );
}

